import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import RGL, {
    // https://github.com/STRML/react-grid-layout#responsive-usage
    // Responsive as ResponsiveRGL,
    WidthProvider as provideWidth,
} from "react-grid-layout"

// components
import { Button, Dashboard as StyledDashboard, EmptyState } from "quorumdesign"
import DashboardHeader from "app/static/frontend/dashboards/containers/DashboardHeader"
import { DashboardEditForm } from "app/static/frontend/dashboards/components/DashboardModalForms"
import LoadingSpinner from "app/static/frontend/components/LoadingSpinner"
import Widget from "app/static/frontend/widgets/containers/Widget"
import WidgetCreateForm from "app/static/frontend/dashboards/components/WidgetCreateForm"

// action creators
import { bulkCreateUserActionPost } from "shared/user-actions/user-actions-action-creators"

// selectors
import { getDasboardWidgetDefaultHeaderIcons } from "app/static/frontend/dashboards/selectors"

// helpers
import { getNestedObjectKey } from "shared/imports/sharedHelperFunctions"

// constants
import { INCORRECT_EXTERNAL_PASSWORD } from "app/static/frontend/components/ExternalPasswordWrapper"

// css
import { StyledDashboardDebug } from "app/static/frontend/dashboards/style"

// action types
import * as actionDicts from "app/static/frontend/dashboards/action-types"

const ReactGridLayout = provideWidth(RGL)
// const ReactGridLayout = provideWidth(ResponsiveRGL)

export class Dashboard extends React.PureComponent {
    async componentDidMount() {
        // we want to await the loadDashboard() call because it invokes
        // this.props.initializeDashboard, which sets editing to false by default
        await this.loadDashboard()
        // we want to check to see if the "editing" query parameter exists in the URI when
        // - directly navigating to /dashboard/:id/?editing=true,
        // - saving and segueing back to a Dashboard from the WidgetEditForm,
        // - etc.

        // this is necessary because, in the above cases, we want to automatically load
        // the Dashboard in Edit mode

        // the URI query parameters are automatically provided to us by react-router through
        // props {
        //     ...
        //     location: {
        //         query: {
        //             editing: "true"
        //         }
        //     }
        //     ...
        // }

        // location is bound to the Dashboard.jsx props in
        // app/static/frontend/index.jsx
        const { query } = this.props.location
        if (query && query.editing && query.editing === "true") {
            this.props.setDashboardsValues({
                id: this.props.id,
                values: {
                    editing: true,
                },
            })
        }
    }

    componentDidUpdate(prevProps) {
        // if the dashboard was previously loaded and is now not loaded,
        // it means we destroyed the Dashboard store and need to re-fetch
        if (!this.props.hasLoaded && prevProps.hasLoaded) {
            this.loadDashboard()
        }
        // for external dashboard's region selection, we need to preload custom region
        // when entering editing mode
        if (this.props.editing && !prevProps.editing) {
            this.props.loadCustomRegions()
        }
    }

    componentWillUnmount() {
        // if you create a new widget, segue back to the Dashboard in Edit mode,
        // click into a different Feature (for example, Issues Management),
        // navigate back to the Dashboard Feature in the Sidebar,
        // and click back into your Dashboard in the Dashboard Resource Table,
        // you should no longer be in Edit mode and any modifications you made without clicking "Done" will be reverted
        if (this.props.editing) {
            this.props.destroyDashboardStore({
                id: this.props.id,
            })
        }

        // widgets can only segue when they are not in edit mode
        if (!this.props.editing) {
            // this is required for any Widget which can segue,
            // as we need to reconcile newLayout with layout after the segue
            // (this ensures that, if the user segues back, the Dashboard looks exactly how it did before the segue)
            this.props.reconcileDashboardLayout({
                id: this.props.id,
            })
        }
    }

    async loadDashboard() {
        if (!this.props.hasLoaded) {
            await this.props
                .loadDashboard(this.props.id)
                .then((response) => {
                    if (response.positions) {
                        this.props.initializeDashboard({
                            cached_widget_data: response.cached_widget_data,
                            creator: response.creator,
                            external_password: response.external_password,
                            external_regions: response.external_regions,
                            external_slug: response.external_slug,
                            externalDownloadsDisabled: response.external_downloads_disabled,
                            id: this.props.id,
                            is_external: response.is_external,
                            isLimitedToCreator: response.is_limited_to_creator,
                            layout: response.positions,
                            limitedToTeams: response.limited_to_teams,
                            name: response.name,
                            widgets: response.widgets,
                        })
                    }
                })
                .catch(async (error) => {
                    if (this.props.isExternal && getNestedObjectKey(error, ["response", "status"]) === 401) {
                        const refresh = await swal(INCORRECT_EXTERNAL_PASSWORD)
                        if (refresh) {
                            document.location.reload()
                        }
                    }
                })
        }
        this.recordUserActions()
    }

    // Fire a UserAction per QDT in the Dashboard
    recordUserActions() {
        const userActions =
            this.props.widgets &&
            Object.values(this.props.widgets)
                .reduce((dataTypes, widget) => {
                    const dataType = getNestedObjectKey(widget, ["data_sources", "0", "safed_search", "data_type"])

                    return !dataTypes.includes(dataType) ? [...dataTypes, dataType] : dataTypes
                }, [])
                .map((dataType) => ({ data_type: dataType }))

        bulkCreateUserActionPost({
            userActionType: DjangIO.app.useractions.types.UserActionType.viewed_dashboard,
            userActions,
        })
    }

    // this renders the actual draggable elements in the ReactGridLayout component
    // https://github.com/STRML/react-grid-layout/blob/master/test/examples/1-basic.jsx
    renderReactGridLayoutItems(items) {
        return (
            items
                // this prevents the Dashboard from trying to render a widget
                // that doesn't exist in the layout, which would break the Dashboard
                // (we also automatically handle this in the Dashboard save(),
                // but this extra check will prevent potential frontend errors)
                .filter((item) => this.props.widgets[item.i])
                .map(
                    (item) =>
                        this.props.widgets[item.i] && (
                            <div
                                key={item.i}
                                className={classNames("react-grid-item-widget", { editing: this.props.editing })}
                            >
                                <Widget
                                    // if we have some preloaded cached_data
                                    cachedData={this.props.cachedWidgetData && this.props.cachedWidgetData[item.i]}
                                    // dashboardId to pass to reconcileDashboardLayout
                                    dashboardId={this.props.id}
                                    editing={this.props.editing}
                                    // since you can add empty widgets,
                                    // we need to set a default message to display
                                    emptyPlaceholder={
                                        this.props.editing
                                            ? `Click on pencil icon in the header to add data to this ` +
                                              `${
                                                  DjangIO.app.widgets.types.DashboardWidgetType.by_value(
                                                      this.props.widgets[item.i].meta.widget_type,
                                                  ).label
                                              }`
                                            : ""
                                    }
                                    evaluateWidget={(widget, slug) => {
                                        if (this.props.isExternal) {
                                            return this.props.evaluateWidget(
                                                {
                                                    slug,
                                                    dashboard: this.props.id,
                                                    additional_params: widget,
                                                },
                                                actionDicts.loadWidgetActionDict,
                                            )
                                        }
                                        return this.props.evaluateWidget(widget, actionDicts.loadWidgetActionDict)
                                    }}
                                    externalDownloadsDisabled={this.props.externalDownloadsDisabled}
                                    // if isExternal is true, we want to fetch data differently
                                    isExternal={this.props.isExternal}
                                    // pass widget schema down so we can
                                    // invoke the widgetEngine and evaluate the qs
                                    widget={this.props.widgets[item.i]}
                                    // StyledWidget props
                                    className="widget"
                                    // default Dashboard-specific headerIcons for all Widgets
                                    headerIcons={getDasboardWidgetDefaultHeaderIcons({
                                        createOrUpdateDashboard: this.props.createOrUpdateDashboard,
                                        deletedWidgets: this.props.deletedWidgets,
                                        editing: this.props.editing,
                                        externalDownloadsDisabled: this.props.externalDownloadsDisabled,
                                        id: this.props.id,
                                        isExternal: this.props.isExternal,
                                        isLoadingDownloadWidget:
                                            this.props.isLoadingDownloadWidgets &&
                                            this.props.isLoadingDownloadWidgets[item.i],
                                        name: this.props.name,
                                        removeFromDashboardLayout: this.props.removeFromDashboardLayout,
                                        segueToUrl: this.props.segueToUrl,
                                        sendAction: this.props.sendAction,
                                        setDashboardsValues: this.props.setDashboardsValues,
                                        submitWidgetTemplateSelection: this.props.submitWidgetTemplateSelection,
                                        widgetKey: item.i,
                                        widgets: this.props.widgets,
                                    })}
                                />
                            </div>
                        ),
                )
        )
    }

    render() {
        if (this.props.isLoading) {
            return <LoadingSpinner id={this.props.isExternal ? "external-loading" : "page-loading-no-top"} />
        }

        return (
            <StyledDashboard
                className="dashboard"
                id={`dashboard-${this.props.id}`}
                dataCy="dashboard"
                editing={this.props.editing}
            >
                {this.props.creatingWidget && (
                    <WidgetCreateForm
                        closeModal={() =>
                            this.props.setDashboardsValues({
                                id: this.props.id,
                                values: {
                                    creatingWidget: false,
                                },
                            })
                        }
                        dashboardId={this.props.id}
                    />
                )}
                {this.props.editingTeamPermissions && (
                    <DashboardEditForm
                        closeModal={() =>
                            this.props.setDashboardsValues({
                                id: this.props.id,
                                values: {
                                    editingTeamPermissions: false,
                                },
                            })
                        }
                        customRegions={this.props.customRegions}
                        dashboardId={this.props.id}
                        external_custom_region_selection={this.props.external_custom_region_selection}
                        external_password={this.props.external_password}
                        external_regions={this.props.external_regions}
                        external_slug={this.props.external_slug}
                        externalDownloadsDisabled={this.props.externalDownloadsDisabled}
                        is_external={this.props.is_external}
                        isEditingTeamPermission={this.props.editingTeamPermissions}
                        isLimitedToCreator={this.props.isLimitedToCreator}
                        limitedToTeams={this.props.limitedToTeams}
                    />
                )}
                <DashboardHeader
                    createOrUpdateDashboard={this.props.createOrUpdateDashboard}
                    dashboardHomepage={this.props.dashboardHomepage}
                    dashboardId={this.props.id}
                    deletedWidgets={this.props.deletedWidgets}
                    destroyDashboardStore={this.props.destroyDashboardStore}
                    editing={this.props.editing}
                    externalDownloadsDisabled={this.props.externalDownloadsDisabled}
                    id={this.props.id}
                    isExternal={this.props.isExternal}
                    isHomepageDashboard={this.props.isHomepageDashboard}
                    isLoadingDownload={this.props.isLoadingDownload}
                    isRefreshing={this.props.isRefreshing}
                    isSaving={this.props.isSaving}
                    locationQuery={this.props.location.query}
                    name={this.props.name}
                    popPage={this.props.popPage}
                    segueToUrl={this.props.segueToUrl}
                    sendAction={this.props.sendAction}
                    setDashboardsValues={this.props.setDashboardsValues}
                    updateUserdata={this.props.updateUserdata}
                    widgets={this.props.widgets}
                />
                {this.props.debug && (
                    <StyledDashboardDebug
                        compactType={this.props.compactType}
                        editing={this.props.editing}
                        id={this.props.id}
                        layout={this.props.layout}
                        setDashboardsValues={this.props.setDashboardsValues}
                    />
                )}
                {this.props.layout && this.props.layout.length ? (
                    <ReactGridLayout
                        breakpoints={this.props.responsive && this.props.breakpoints}
                        // the total number of columns in the grid
                        cols={this.props.responsive ? this.props.colsResponsive : this.props.cols}
                        className={this.props.className}
                        // whether the grid should be weighted vertically, horizontally, or freely
                        compactType={
                            DjangIO.app.widgets.types.ReactGridLayoutTypes.by_value(this.props.compactType).compactType
                        }
                        // this completely disables the drag functionality for a GridItem
                        // this is necessary because, simply returning false in onDragStart
                        // still appends the react-draggable-dragging className to our Widget
                        // react-grid-item container, which means that when we enter edit mode,
                        // any widget that had been previously clicked in non-edit mode will be
                        // "stuck" in its previous position
                        draggableHandle={!this.props.editing ? ".not-draggable" : null}
                        // this is our internal redux store value that tracks whether we have clicked the edit button
                        editing={this.props.editing}
                        // id of the Dashboard from the URI
                        id={this.props.id}
                        // whether or not grid items can be dragged and resized
                        // this is necessary because conditionally setting isDraggable and isResizable
                        // causes remounts of n widgets whenever either prop changes
                        // https://github.com/STRML/react-grid-layout/issues/89
                        // https://github.com/STRML/react-grid-layout/issues/240
                        // https://github.com/STRML/react-grid-layout/issues/362
                        // https://github.com/STRML/react-grid-layout/issues/371
                        // https://github.com/STRML/react-grid-layout/issues/379
                        // https://github.com/STRML/react-grid-layout/issues/626
                        // https://github.com/STRML/react-grid-layout/issues/721
                        // https://github.com/STRML/react-grid-layout/issues/756
                        // https://github.com/STRML/react-grid-layout/issues/1067
                        // https://github.com/STRML/react-grid-layout/pull/892#issuecomment-532014140
                        isDraggable
                        isResizable
                        // this is the current state of the grid layout
                        layout={!this.props.responsive && this.props.layout}
                        // this is the current state of the grid layout
                        layouts={this.props.responsive && { lg: this.props.layout }}
                        // margin between widgets
                        margin={[this.props.margin, this.props.margin]}
                        // measure the container's width before mounting children
                        // we use this to completely eliminate any resizing animation on application/component mount
                        measureBeforeMount
                        // needed for the same reason as the editing prop above
                        // if we are editing, we want the default onDragStart functionality
                        // if we are not editing, then we return false to disable the internal GridItem onDrag
                        onDragStart={() => this.props.editing}
                        onLayoutChange={(newLayout) => {
                            this.props.setDashboardsValues({
                                id: this.props.id,
                                values: {
                                    newLayout,
                                },
                            })
                        }}
                        onResizeStop={(layout, oldItem, newItem, placeholder, e, element) => {
                            const visSubtype = this.props.widgets[newItem.i].meta.vis_subtype

                            // if we resize a Widget and its height is now higher than the previous
                            // widget content, and we can infinite scroll, we want to fire the scroll event
                            if (
                                this.props.widgets[newItem.i].meta.widget_type ===
                                DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value
                            ) {
                                // app/static/frontend/widgets/components/ListWidget.jsx
                                // implements the react-virtualized CellMeasurer,
                                // which needs to reflow content (list.recomputeRowHeights())
                                // and explicitly update the cache (cache.clearAll())
                                // whenever the contents of the ListWidget shift,
                                // like when we resize the browser window (Dashboard.jsx, WidgetEditForm.jsx)
                                // or resize the ListWidget (Dashboard.jsx)
                                window.dispatchEvent(new Event("resize"))
                            } else if (
                                [
                                    // Bar, BarGrouped, BarStacked
                                    DjangIO.app.widgets.types.VisualizationSubtype.bar.value,
                                    // Line
                                    DjangIO.app.widgets.types.VisualizationSubtype.line.value,
                                    // Map
                                    DjangIO.app.widgets.types.VisualizationSubtype.map.value,
                                    // Pie
                                    DjangIO.app.widgets.types.VisualizationSubtype.pie.value,
                                ].includes(visSubtype)
                            ) {
                                // Since all four components use the useRefDimensions hook,
                                // which sets a resize event listener on the window
                                // (and automatically returns the new height/width on resize),
                                // by manually invoking that resize event listener,
                                // we are able to trigger a rerender/rememo of any necessary
                                // d3 DOM nodes in the component.

                                // The four components referenced above will all trigger their corresponding
                                // useEffect [colors, height, width] hook
                                // when the above height/width recomputation in the useRefDimensions hook occurs.

                                // tl;dr: when you resize a Dashboard widget,
                                // we should update the height/width of the svg ref and repaint
                                // necessary portions of the visualization
                                window.dispatchEvent(new Event("resize"))
                            }
                        }}
                        // this turns off rearrangement so items will not be pushed around
                        // preventCollision
                        // the height of each row, in pixels
                        rowHeight={this.props.rowHeight}
                    >
                        {this.renderReactGridLayoutItems(this.props.layout)}
                    </ReactGridLayout>
                ) : (
                    // if the user has deleted a widget,
                    // we should force them to save before they are able to create a new widget
                    (!this.props.deletedWidgets || !this.props.deletedWidgets.length) && (
                        <EmptyState
                            empty_state_paragraph={
                                "Every widget is auto-updating and can be customized based on " + "your specific needs."
                            }
                            empty_state_title="Add New Widget"
                            icon="sliders-h-square"
                            iconFamily="fad"
                        >
                            <Button
                                dataCy="dashboard-empty-state-create-button"
                                leftIcon="plus"
                                onClick={() =>
                                    this.props.setDashboardsValues({
                                        id: this.props.id,
                                        values: {
                                            creatingWidget: true,
                                        },
                                    })
                                }
                                text="Add Widget"
                                type="filled"
                            />
                            <Button
                                dataCy="dashboard-empty-state-help-button"
                                onClick={() => this.props.segueToUrl("/help/how-to/644/")}
                                type="outlined"
                                text="Learn More"
                            />
                        </EmptyState>
                    )
                )}
            </StyledDashboard>
        )
    }
}

Dashboard.defaultProps = {
    breakpoints: {
        lg: 1200,
        md: 996,
        sm: 768,
        xs: 480,
        xxs: 0,
    },
    className: "layout",
    cols: 6,
    colsResponsive: {
        lg: 6,
        md: 5,
        sm: 4,
        xs: 2,
        xxs: 1,
    },
    creatingWidget: false,
    compactType: DjangIO.app.widgets.types.ReactGridLayoutTypes.vertical.value,
    debug: false,
    deletedWidgets: [],
    isExternal: false,
    margin: 24,
    responsive: false,
    rowHeight: 13,
}

Dashboard.propTypes = {
    // inherited from Index.jsx
    id: PropTypes.string,
    location: PropTypes.object,
    params: PropTypes.object,
    route: PropTypes.object,
    router: PropTypes.object,
    routeParams: PropTypes.object,
    routes: PropTypes.array,

    // defaultProps
    breakpoints: PropTypes.object,
    className: PropTypes.string,
    cols: PropTypes.number,
    colsResponsive: PropTypes.object,
    creatingWidget: PropTypes.bool,
    debug: PropTypes.bool,
    isExternal: PropTypes.bool,
    margin: PropTypes.number,
    responsive: PropTypes.bool,
    rowHeight: PropTypes.number,

    // action creators
    createOrUpdateDashboard: PropTypes.func,
    destroyDashboardStore: PropTypes.func,
    initializeDashboard: PropTypes.func,
    loadDashboard: PropTypes.func,
    popPage: PropTypes.func,
    reconcileDashboardLayout: PropTypes.func,
    removeFromDashboardLayout: PropTypes.func,
    segueToUrl: PropTypes.func,
    sendAction: PropTypes.func,
    setDashboardsValues: PropTypes.func,
    submitWidgetTemplateSelection: PropTypes.func,
    updateUserdata: PropTypes.func,

    // store
    cachedWidgetData: PropTypes.object,
    compactType: PropTypes.number,
    deletedWidgets: PropTypes.array,
    editing: PropTypes.bool,
    editingTeamPermissions: PropTypes.bool,
    externalDownloadsDisabled: PropTypes.bool,
    hasLoaded: PropTypes.bool,
    isHomepageDashboard: PropTypes.bool,
    isLimitedToCreator: PropTypes.bool,
    isLoading: PropTypes.bool,
    isLoadingDownload: PropTypes.bool,
    isLoadingDownloadWidgets: PropTypes.object,
    isRefreshing: PropTypes.bool,
    isSaving: PropTypes.bool,
    // this is the internal layout that react-grid-layout generates
    // and modifies when you drag and resize widgets around.
    // https://github.com/STRML/react-grid-layout#grid-item-props
    // i: string, // a string corresponding to the component key
    // x: int,
    // y: int,
    // w: int,
    // h: int,
    // minW: ?int = 0,
    // maxW: ?int = Infinity,
    // minH: ?int = 0,
    // maxH: ?int = Infinity,
    layout: PropTypes.array,
    limitedToTeams: PropTypes.array,
    name: PropTypes.string,
    widgets: PropTypes.object,

    // passed from LandingPageOrDashboard
    dashboardHomepage: PropTypes.bool,
}

export default Dashboard
